import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import client from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import AccessDenied from "@/components/AccessDenied";
import { KpiCard } from "@/components/KpiCard";
import AIInsightsPanel from "@/components/AIInsightsPanel";
import { RevenueVsBudgetChart, SectorRevenueChart, CashFlowChart, RevenueBridgeChart } from "@/components/Charts";
import NewsWidget from "@/components/NewsWidget";
import LiveActivity from "@/components/LiveActivity";
import NewsTicker from "@/components/NewsTicker";
import { fmtMoney, fmtPctPlain, fmtNum } from "@/lib/format";
import { ArrowRight, Warning, TrendUp, TrendDown, Rocket, FilePdf, Presentation } from "@phosphor-icons/react";

export default function Overview() {
    const [data, setData] = useState(null);
    const [denied, setDenied] = useState(false);
    const [period, setPeriod] = useState("MTD"); // MTD | YTD
    useEffect(() => {
        client.get("/dashboard/overview")
            .then((r) => setData(r.data))
            .catch((e) => { if (e.response?.status === 403) setDenied(true); });
    }, []);
    if (denied) return <AccessDenied />;

    if (!data) return <LoadingState />;
    const s = data.snapshot;

    // MTD vs YTD selector — YTD sums the monthly trend arrays (up to the latest month)
    const sumTrend = (arr) => arr.reduce((a, b) => a + b, 0);
    const isYTD = period === "YTD";
    const revenueVal = isYTD ? sumTrend(s.revenue.trend) : s.revenue.value_musd;
    const ebitdaVal = isYTD ? sumTrend(s.ebitda.trend) : s.ebitda.value_musd;
    const netIncomeVal = isYTD ? sumTrend(s.net_income.trend) : s.net_income.value_musd;
    const fcfVal = isYTD ? sumTrend(s.free_cash_flow.trend) : s.free_cash_flow.value_musd;

    // Business Performance ranking (by YoY growth)
    const sectors = [...data.sector_breakdown].sort((a, b) => b.yoy_growth_pct - a.yoy_growth_pct);
    const topPerf = sectors[0];
    const watchPerf = sectors[Math.floor(sectors.length / 2)];
    const underPerf = sectors[sectors.length - 1];

    return (
        <div>
            <NewsTicker />
            <PageHeader
                overline="Group Consolidated · 168 entities · 7 divisions · 16 jurisdictions"
                title="Executive Overview"
                description="Consolidated group performance with concise CFO-level narrative and recommended actions."
                period={s.period_label}
            >
                <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
                    <div className="flex border border-fp-border" data-testid="period-toggle">
                        {["MTD", "YTD"].map((p) => (
                            <button key={p} data-testid={`period-${p}`} onClick={() => setPeriod(p)} className={`px-4 py-1.5 font-mono text-xs ${period === p ? "bg-fp-ink text-white" : "text-fp-secondary hover:text-fp-ink"}`}>{p}</button>
                        ))}
                    </div>
                    <Link to="/board-meeting" data-testid="launch-board-meeting" className="inline-flex items-center gap-2 border border-fp-ink text-fp-ink px-3 sm:px-4 py-2 font-sans text-xs hover:bg-fp-panel transition-colors" title="Full-screen board briefing">
                        <Presentation size={12} weight="fill" /> <span className="hidden sm:inline">Board Meeting Mode</span><span className="sm:hidden">Present</span>
                    </Link>
                    <Link to="/executive-pack" data-testid="view-exec-pack" className="inline-flex items-center gap-2 bg-fp-ink text-white px-3 sm:px-4 py-2 font-sans text-xs hover:bg-fp-ink-hover transition-colors">
                        <FilePdf size={12} weight="fill" /> <span className="hidden sm:inline">View Executive Pack</span><span className="sm:hidden">Exec Pack</span>
                    </Link>
                </div>
            </PageHeader>

            <div className="px-4 sm:px-6 lg:px-12 py-10 space-y-10">
                {/* KPI row */}
                <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 lg:gap-5" data-testid="overview-kpis">
                    <KpiCard label={`Revenue · ${period}`} rawValue={revenueVal} prefix="$" valueSuffix="M" subValue={`vs Budget: ${fmtPctPlain(s.revenue.budget_variance_pct)}`} delta={s.revenue.yoy_pct} deltaLabel="YoY" trend={s.revenue.trend} testId="kpi-revenue" variant="primary" chip={s.revenue.yoy_pct >= 0 ? "GROWTH" : null} />
                    <KpiCard label={`EBITDA · ${period}`} rawValue={ebitdaVal} prefix="$" valueSuffix="M" subValue={`Margin: ${fmtPctPlain(s.ebitda.margin_pct)}`} delta={s.ebitda.mom_pct} deltaLabel="MoM" trend={s.ebitda.trend} testId="kpi-ebitda" variant="success" />
                    <KpiCard label={`Net Income · ${period}`} rawValue={netIncomeVal} prefix="$" valueSuffix="M" trend={s.net_income.trend} testId="kpi-netincome" variant="teal" />
                    <KpiCard label={`Free Cash Flow · ${period}`} rawValue={fcfVal} prefix="$" valueSuffix="M" trend={s.free_cash_flow.trend} testId="kpi-fcf" variant="plum" />
                    <KpiCard label="Return on Invested Capital" rawValue={s.roic_pct} decimals={1} valueSuffix="%" subValue={`ROE: ${fmtPctPlain(s.roe_pct)}`} testId="kpi-roic" variant="warn" />
                </div>

                {/* Business Performance */}
                <div data-testid="business-perf">
                    <div className="font-sans text-[11px] uppercase tracking-[0.2em] text-fp-muted mb-3">Business Performance</div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <BusinessPerfCard label="Top Performer" name={topPerf.sector_name} metric={`${fmtPctPlain(topPerf.yoy_growth_pct)} YoY · ${fmtPctPlain(topPerf.ebitda_margin_pct)} margin`} tone="success" Icon={TrendUp} link={`/sector/${topPerf.sector_id}`} />
                        <BusinessPerfCard label="Watchlist" name={watchPerf.sector_name} metric={`${fmtPctPlain(watchPerf.yoy_growth_pct)} YoY · ${fmtPctPlain(watchPerf.ebitda_margin_pct)} margin`} tone="warn" Icon={Warning} link={`/sector/${watchPerf.sector_id}?watchlist=1`} />
                        <BusinessPerfCard label="Underperformer" name={underPerf.sector_name} metric={`${fmtPctPlain(underPerf.yoy_growth_pct)} YoY · ${fmtPctPlain(underPerf.ebitda_margin_pct)} margin`} tone="danger" Icon={TrendDown} link={`/sector/${underPerf.sector_id}?watchlist=1`} testId="underperf-link" />
                    </div>
                </div>

                {/* Transformation Portfolio */}
                <div data-testid="transformation-portfolio">
                    <div className="font-sans text-[11px] uppercase tracking-[0.2em] text-fp-muted mb-3">Transformation Portfolio</div>
                    <div className="bg-white border border-fp-border p-6">
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 mb-6 pb-6 border-b border-fp-border">
                            <div><div className="font-mono text-2xl text-fp-ink">4</div><div className="font-sans text-[10px] uppercase tracking-widest text-fp-muted mt-1">Active Initiatives</div></div>
                            <div><div className="font-mono text-2xl text-fp-ink">$55M + 30%</div><div className="font-sans text-[10px] uppercase tracking-widest text-fp-muted mt-1">Target Value</div></div>
                            <div><div className="font-mono text-2xl text-fp-success">50%</div><div className="font-sans text-[10px] uppercase tracking-widest text-fp-muted mt-1">On Track</div></div>
                        </div>
                        <div className="space-y-3">
                            {[
                                { n: "Working Capital Optimisation", s: "On Track", e: "$25M", r: "$14M", pct: 56, t: "success" },
                                { n: "Procurement Efficiency", s: "At Risk", e: "$12M", r: "$4M", pct: 33, t: "warn" },
                                { n: "Reporting Automation", s: "On Track", e: "30% effort reduction", r: "18%", pct: 60, t: "success" },
                                { n: "Commercial Productivity", s: "Delayed", e: "$18M", r: "$6M", pct: 33, t: "danger" },
                            ].map((i) => (
                                <div key={i.n} className="flex items-center gap-4">
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-baseline justify-between mb-1"><div className="font-sans text-sm text-fp-ink">{i.n}</div><div className="font-mono text-xs text-fp-secondary">{i.r} / {i.e}</div></div>
                                        <div className="h-1.5 bg-fp-border"><div className={`h-full ${i.t === "success" ? "bg-fp-success" : i.t === "warn" ? "bg-fp-saffron" : "bg-fp-terracotta"}`} style={{ width: `${i.pct}%` }} /></div>
                                    </div>
                                    <span className={`shrink-0 px-2 py-0.5 font-sans text-[10px] uppercase tracking-wider ${i.t === "success" ? "text-fp-success bg-fp-success/10" : i.t === "warn" ? "text-fp-saffron bg-fp-saffron/10" : "text-fp-terracotta bg-fp-terracotta/10"}`}>{i.s}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* AI Insights + primary chart + Live Activity */}
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                    <div className="lg:col-span-4">
                        <AIInsightsPanel context="group" contextLabel="Group" />
                    </div>
                    <div className="lg:col-span-5 bg-white border border-fp-border p-5 sm:p-6 lg:p-8" data-testid="chart-rev-bridge">
                        <div className="flex items-baseline justify-between mb-6">
                            <div>
                                <div className="font-sans text-[11px] uppercase tracking-[0.2em] text-fp-muted mb-1">Revenue Bridge</div>
                                <h3 className="font-serif text-2xl text-fp-ink">Prior Year → Actual · Volume · Price · FX</h3>
                            </div>
                        </div>
                        <RevenueBridgeChart revenue={data.consolidated.pnl.revenue} priorYear={data.consolidated.pnl.prior_year_revenue} budget={data.consolidated.pnl.budget_revenue} />
                    </div>
                    <div className="lg:col-span-3">
                        <LiveActivity />
                    </div>
                </div>

                {/* News widget row */}
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                    <div className="lg:col-span-12">
                        <NewsWidget scope="group" />
                    </div>
                </div>

                {/* Secondary KPI row: People, ESG, Risk */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-5">
                    <KpiCard label="Headcount" value={fmtNum(s.headcount)} subValue={`Attrition: ${fmtPctPlain(s.attrition_pct)}`} testId="kpi-headcount" />
                    <KpiCard label="Customer NPS" value={fmtNum(s.nps)} testId="kpi-nps" />
                    <KpiCard label="Emissions" value={fmtNum(s.emissions_tco2e / 1000, 1) + "k"} unit="tCO₂e" testId="kpi-emissions" />
                    <KpiCard label="Safety LTIFR" value={s.safety_ltifr.toFixed(2)} testId="kpi-safety" />
                </div>

                {/* Sector breakdown */}
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                    <div className="lg:col-span-7 bg-white border border-fp-border p-5 sm:p-6 lg:p-8" data-testid="sector-breakdown">
                        <div className="flex items-baseline justify-between mb-6">
                            <div>
                                <div className="font-sans text-[11px] uppercase tracking-[0.2em] text-fp-muted mb-1">Sector Contribution</div>
                                <h3 className="font-serif text-2xl text-fp-ink">Revenue by Sector</h3>
                            </div>
                        </div>
                        <SectorRevenueChart sectors={data.sector_breakdown} />
                        <div className="overflow-x-auto mt-6 -mx-2 sm:mx-0">
                        <table className="w-full min-w-[520px]">
                            <thead>
                                <tr className="border-b-2 border-fp-ink">
                                    <th className="text-left pb-2 font-sans text-[10px] uppercase tracking-wider text-fp-secondary">Sector</th>
                                    <th className="text-right pb-2 font-sans text-[10px] uppercase tracking-wider text-fp-secondary">Revenue</th>
                                    <th className="text-right pb-2 font-sans text-[10px] uppercase tracking-wider text-fp-secondary">EBITDA</th>
                                    <th className="text-right pb-2 font-sans text-[10px] uppercase tracking-wider text-fp-secondary">Margin</th>
                                    <th className="text-right pb-2 font-sans text-[10px] uppercase tracking-wider text-fp-secondary">YoY</th>
                                    <th className="pb-2" />
                                </tr>
                            </thead>
                            <tbody>
                                {data.sector_breakdown.map((s2) => (
                                    <tr key={s2.sector_id} className="border-b border-fp-border hover:bg-fp-bg transition-colors">
                                        <td className="py-3 font-sans text-sm text-fp-ink">{s2.sector_name}</td>
                                        <td className="py-3 text-right font-mono text-sm text-fp-ink">{fmtMoney(s2.revenue_musd)}</td>
                                        <td className="py-3 text-right font-mono text-sm text-fp-ink">{fmtMoney(s2.ebitda_musd)}</td>
                                        <td className="py-3 text-right font-mono text-sm text-fp-secondary">{fmtPctPlain(s2.ebitda_margin_pct)}</td>
                                        <td className={`py-3 text-right font-mono text-sm ${s2.yoy_growth_pct >= 0 ? 'text-fp-success' : 'text-fp-terracotta'}`}>{s2.yoy_growth_pct >= 0 ? '+' : ''}{s2.yoy_growth_pct}%</td>
                                        <td className="py-3 text-right">
                                            <Link to={`/sector/${s2.sector_id}`} data-testid={`drill-sector-${s2.sector_id}`} className="text-fp-secondary hover:text-fp-ink"><ArrowRight size={14} /></Link>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        </div>
                    </div>

                    <div className="lg:col-span-5 bg-white border border-fp-border p-5 sm:p-6 lg:p-8" data-testid="cash-flow-chart">
                        <div className="mb-6">
                            <div className="font-sans text-[11px] uppercase tracking-[0.2em] text-fp-muted mb-1">Cash Generation</div>
                            <h3 className="font-serif text-2xl text-fp-ink">OCF · CapEx · FCF</h3>
                        </div>
                        <CashFlowChart periods={data.periods} ocf={data.consolidated.cash_flow.operating_cash_flow} capex={data.consolidated.cash_flow.capex} fcf={data.consolidated.cash_flow.free_cash_flow} />
                    </div>
                </div>

                {/* Heatmap */}
                <div className="bg-white border border-fp-border p-4 sm:p-6 lg:p-8" data-testid="performance-heatmap">
                    <div className="mb-6">
                        <div className="font-sans text-[11px] uppercase tracking-[0.2em] text-fp-muted mb-1">Cross-Portfolio</div>
                        <h3 className="font-serif text-xl sm:text-2xl text-fp-ink">Sector × Country Performance</h3>
                        <p className="font-sans text-xs sm:text-sm text-fp-secondary mt-2">Revenue intensity by geography; darker cells indicate stronger contribution.</p>
                    </div>
                    <div className="overflow-x-auto -mx-2 sm:mx-0">
                        <HeatmapTable heatmap={data.heatmap} countries={data.country_breakdown} />
                    </div>
                </div>

                {/* Country breakdown */}
                <div className="bg-white border border-fp-border p-5 sm:p-6 lg:p-8" data-testid="country-breakdown">
                    <div className="mb-6">
                        <div className="font-sans text-[11px] uppercase tracking-[0.2em] text-fp-muted mb-1">Geographic Diversification</div>
                        <h3 className="font-serif text-2xl text-fp-ink">Revenue by Country</h3>
                    </div>
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                        {data.country_breakdown.map((c) => (
                            <Link to={`/country/${c.country_code}`} key={c.country_code} data-testid={`drill-country-${c.country_code}`} className="border border-fp-border p-5 hover:border-fp-ink transition-colors group">
                                <div className="flex items-baseline justify-between mb-2">
                                    <div className="font-sans text-sm text-fp-ink font-medium">{c.country_name}</div>
                                    <div className="font-mono text-[10px] text-fp-muted uppercase">{c.region}</div>
                                </div>
                                <div className="font-mono text-2xl text-fp-ink">{fmtMoney(c.revenue_musd, { decimals: 0 })}</div>
                                <div className="font-mono text-xs text-fp-secondary mt-1">EBITDA {fmtMoney(c.ebitda_musd, { decimals: 0 })} · {c.entity_count} entities</div>
                            </Link>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}

const HeatmapTable = ({ heatmap, countries }) => {
    const maxRev = Math.max(...heatmap.flatMap((r) => r.cells.map((c) => c.revenue_musd || 0)));
    const shade = (v) => {
        if (v == null) return { bg: "#F3F1EC", color: "#8A938D" };
        const intensity = Math.max(0.15, v / maxRev);
        return { bg: `rgba(10, 42, 34, ${intensity})`, color: intensity > 0.5 ? "#FFF" : "#0A2A22" };
    };
    return (
        <div className="overflow-x-auto">
            <table className="w-full">
                <thead>
                    <tr className="border-b-2 border-fp-ink">
                        <th className="text-left pb-2 font-sans text-[10px] uppercase tracking-wider text-fp-secondary min-w-[180px]">Sector</th>
                        {countries.map((c) => (
                            <th key={c.country_code} className="text-center pb-2 font-mono text-[10px] uppercase tracking-wider text-fp-secondary">{c.country_code}</th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {heatmap.map((row) => (
                        <tr key={row.sector_id}>
                            <td className="py-1 font-sans text-sm text-fp-ink pr-4">{row.sector_name}</td>
                            {row.cells.map((cell, i) => {
                                const st = shade(cell.revenue_musd);
                                return (
                                    <td key={i} className="p-1">
                                        <div className="h-14 flex flex-col items-center justify-center transition-all hover:scale-[1.03] cursor-default" style={{ backgroundColor: st.bg, color: st.color }}>
                                            {cell.revenue_musd != null ? (
                                                <>
                                                    <div className="font-mono text-sm font-medium">${cell.revenue_musd.toFixed(0)}M</div>
                                                    <div className="font-mono text-[10px] opacity-70">{cell.ebitda_margin_pct.toFixed(0)}%</div>
                                                </>
                                            ) : <div className="font-mono text-[10px] text-fp-muted">—</div>}
                                        </div>
                                    </td>
                                );
                            })}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

const LoadingState = () => (
    <div className="p-16">
        <div className="font-serif text-3xl text-fp-ink">Loading…</div>
        <div className="font-sans text-sm text-fp-secondary mt-2">Consolidating entity submissions</div>
    </div>
);

const BusinessPerfCard = ({ label, name, metric, tone, Icon, link, testId }) => {
    const border = tone === "success" ? "border-l-fp-success" : tone === "warn" ? "border-l-fp-saffron" : "border-l-fp-terracotta";
    const chipCls = tone === "success" ? "text-fp-success bg-fp-success/10" : tone === "warn" ? "text-fp-saffron bg-fp-saffron/10" : "text-fp-terracotta bg-fp-terracotta/10";
    return (
        <Link to={link} data-testid={testId} className={`block p-5 border-l-4 ${border} bg-white border border-fp-border hover:shadow-sm transition-shadow`}>
            <div className="flex items-center justify-between mb-2">
                <div className="font-sans text-[10px] uppercase tracking-[0.25em] text-fp-muted">{label}</div>
                <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 font-sans text-[10px] tracking-wider uppercase ${chipCls}`}><Icon size={11} weight="fill" />{tone === "success" ? "Strong" : tone === "warn" ? "Watch" : "Weak"}</span>
            </div>
            <div className="font-serif text-xl text-fp-ink">{name}</div>
            <div className="font-mono text-xs text-fp-secondary mt-1">{metric}</div>
        </Link>
    );
};
